// @ts-nocheck
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, Clock, CheckCircle2, Eye, EyeOff, Loader2, PartyPopper } from 'lucide-react';

const ROLE_LABEL: Record<string, string> = {
  rlevents_super_admin: 'Super Admin',
  rlevents_tenant_admin: 'Organization Admin',
  rlevents_tenant_staff: 'Staff',
};

const signupSchema = z
  .object({
    password: z.string().min(8, 'At least 8 characters'),
    confirmPassword: z.string(),
    fullName: z.string().max(100).optional(),
    agree: z.literal(true, { errorMap: () => ({ message: 'You must agree to continue' }) }),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords must match',
  });
type SignupValues = z.infer<typeof signupSchema>;

export default function AcceptAdminInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();

  const [accepted, setAccepted] = useState<{ role: string } | null>(null);
  
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [emailExists, setEmailExists] = useState(false);

  const { data: preview, isLoading, error } = useQuery({
    queryKey: ['admin-invite-preview', token],
    queryFn: async () => {
      const { data, error: fnErr } = await (supabase as any).functions.invoke(
        'rle-accept-admin-invite',
        { body: { token, preview: true } }
      );
      if (fnErr) throw fnErr;
      if (!data?.ok) {
        const err = new Error(data?.error?.message ?? 'Invalid invitation');
        (err as any).code = data?.error?.code ?? 'TOKEN_INVALID';
        (err as any).expires_at = data?.error?.context?.expires_at;
        throw err;
      }
      return data.data;
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const { data, error: fnErr } = await (supabase as any).functions.invoke(
        'rle-accept-admin-invite',
        { body: { token } }
      );
      if (fnErr) throw fnErr;
      if (!data?.ok) throw new Error(data?.error?.message ?? 'Failed to accept');
      return data.data;
    },
    onSuccess: async (data) => {
      if (data.session?.access_token && data.session?.refresh_token) {
        await (supabase as any).auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }
      setAccepted({ role: preview.role });
      toast.success('Welcome to RLevents');
      setTimeout(() => navigate(data.redirect_path ?? '/'), 1200);
    },
  });

  const signupForm = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: { password: '', confirmPassword: '', fullName: '', agree: undefined as any },
  });

  const EMAIL_EXISTS_MESSAGE =
    "An account already exists for this email — please use 'Sign in' below to accept this invitation.";

  // Detects the "user already registered / already exists" case from any shape
  // the backend might return (clean 409/EMAIL_EXISTS body, or a raw 500 error).
  const looksLikeEmailExists = (status?: number, code?: string, message?: string) => {
    if (status === 409) return true;
    if (code && ['EMAIL_EXISTS', 'USER_ALREADY_EXISTS', 'EMAIL_ALREADY_EXISTS'].includes(code)) {
      return true;
    }
    return /already (registered|exists|been registered)|user already|email.*exists/i.test(
      message ?? ''
    );
  };

  const handleSignup = async (values: SignupValues) => {
    setSignupError(null);
    setEmailExists(false);
    try {
      const { data, error: fnErr } = await (supabase as any).functions.invoke(
        'rle-accept-admin-invite',
        {
          body: {
            token,
            auth_action: 'create_account',
            password: values.password,
            full_name: values.fullName || null,
          },
        }
      );

      // Non-2xx responses surface as fnErr; the body lives on fnErr.context (a Response).
      if (fnErr) {
        let status: number | undefined = fnErr?.context?.status;
        let code: string | undefined;
        let message: string | undefined = fnErr?.message;
        try {
          const bodyText = await fnErr?.context?.text?.();
          if (bodyText) {
            const parsed = JSON.parse(bodyText);
            code = parsed?.error?.code ?? parsed?.code;
            message = parsed?.error?.message ?? parsed?.message ?? message;
          }
        } catch {
          /* body not JSON — fall back to status/message heuristics */
        }
        if (looksLikeEmailExists(status, code, message)) {
          setEmailExists(true);
          setSignupError(EMAIL_EXISTS_MESSAGE);
          return;
        }
        throw new Error(message ?? 'Failed to create account');
      }

      if (!data?.ok) {
        const code = data?.error?.code;
        const message = data?.error?.message;
        if (looksLikeEmailExists(undefined, code, message)) {
          setEmailExists(true);
          setSignupError(EMAIL_EXISTS_MESSAGE);
          return;
        }
        throw new Error(message ?? 'Failed to create account');
      }

      const result = data.data;
      if (result.session?.access_token && result.session?.refresh_token) {
        await (supabase as any).auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });
      } else {
        await supabase.auth.signInWithPassword({
          email: preview.invitee_email,
          password: values.password,
        });
      }
      toast.success('Welcome to RLevents!');
      navigate(result.redirect_path ?? '/');
    } catch (e: any) {
      setSignupError(e.message ?? 'Something went wrong');
    }
  };

  const Wrapper = ({ children }: { children: React.ReactNode }) => (
    <div className="min-h-screen bg-background flex items-start justify-center px-4 pt-36 pb-12 sm:pt-40 sm:pb-20">
      <div className="w-full max-w-[480px] animate-in fade-in duration-500">
        <div className="text-center mb-6">
          <span className="text-xl font-bold text-secondary tracking-tight">RLevents</span>
        </div>
        {children}
      </div>
    </div>
  );

  if (isLoading || authLoading) {
    return (
      <Wrapper>
        <Card>
          <CardContent className="p-8 space-y-4">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading invitation...</span>
            </div>
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </Wrapper>
    );
  }

  if (error) {
    const code = (error as any).code as string;
    const expiresAt = (error as any).expires_at;
    let icon = <AlertTriangle className="h-12 w-12 text-destructive" />;
    let title = 'Invalid invitation link';
    let body = 'This invitation link is not valid. Please ask whoever invited you to send a new one.';
    let btnLabel = 'Go to RLevents home';
    let btnAction = () => navigate('/');

    if (code === 'TOKEN_EXPIRED') {
      icon = <Clock className="h-12 w-12 text-destructive" />;
      title = 'Invitation expired';
      const dateStr = expiresAt ? new Date(expiresAt).toLocaleDateString() : 'an earlier date';
      body = `This invitation expired on ${dateStr}. Ask the inviter to send a new one.`;
    } else if (code === 'TOKEN_ALREADY_USED') {
      icon = <CheckCircle2 className="h-12 w-12 text-green-600" />;
      title = 'Invitation already accepted';
      body = 'This invitation has already been used. Sign in to access RLevents.';
      btnLabel = 'Sign in';
      btnAction = () => navigate('/login');
    }

    return (
      <Wrapper>
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <div className="flex justify-center">{icon}</div>
            <h1 className="text-xl font-semibold">{title}</h1>
            <p className="text-sm text-muted-foreground">{body}</p>
            <Button onClick={btnAction} className="w-full">
              {btnLabel}
            </Button>
          </CardContent>
        </Card>
      </Wrapper>
    );
  }

  if (!preview) return null;

  if (accepted) {
    return (
      <Wrapper>
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <PartyPopper className="h-12 w-12 text-secondary mx-auto" />
            <h1 className="text-2xl font-bold">Welcome to RLevents!</h1>
            <p className="text-sm text-muted-foreground">Taking you to your dashboard...</p>
          </CardContent>
        </Card>
      </Wrapper>
    );
  }

  const inviteeEmail: string = preview.invitee_email ?? '';
  const rawInviteeName: string = preview.invitee_name ?? '';
  // Never put an email in the Full name field
  const inviteeName: string = rawInviteeName && !rawInviteeName.includes('@') ? rawInviteeName : '';
  const inviter = preview.inviter ?? {};
  const role = preview.role as string;
  const org = preview.organization;

  let title = "You're invited to RLevents";
  if (role === 'rlevents_super_admin') title = 'Welcome to RLevents';
  else if (org?.name) title = `You've been invited to manage ${org.name}`;


  const emailMatches =
    user?.email && user.email.toLowerCase() === inviteeEmail.toLowerCase();

  return (
    <Wrapper>
      <Card>
        <CardContent className="p-6 sm:p-8 space-y-5">
          <div>
            <div className="text-3xl mb-2">🏀</div>
            <h1 className="text-[22px] font-bold leading-tight">{title}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Role: {ROLE_LABEL[role] ?? role}
              {inviter?.name ? ` · Invited by ${inviter.name}` : ''}
            </p>
          </div>

          {org && (
            <div className="bg-muted/50 rounded-lg p-4 flex items-center gap-3">
              {org.logo_url ? (
                <img src={org.logo_url} alt={org.name} className="h-12 w-12 rounded-md object-cover" />
              ) : (
                <div className="h-12 w-12 rounded-md bg-secondary/20 flex items-center justify-center text-secondary font-bold">
                  {org.name?.[0]?.toUpperCase() ?? '?'}
                </div>
              )}
              <div>
                <div className="font-semibold text-sm">{org.name}</div>
                <div className="text-xs text-muted-foreground">Organization</div>
              </div>
            </div>
          )}

          {preview.personal_message && (
            <div className="border-l-4 border-secondary bg-muted/30 rounded p-3">
              <p className="text-sm italic text-foreground/80">"{preview.personal_message}"</p>
            </div>
          )}

          {user && emailMatches && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Signed in as {user.email}</p>
              {acceptMutation.isError && (
                <Alert variant="destructive">
                  <AlertDescription className="flex items-center justify-between gap-2">
                    <span>{(acceptMutation.error as Error)?.message ?? 'Unknown error'}</span>
                    <Button size="sm" variant="outline" onClick={() => acceptMutation.mutate()}>
                      Try again
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
              <Button
                size="lg"
                className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                disabled={acceptMutation.isPending}
                onClick={() => acceptMutation.mutate()}
              >
                {acceptMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Accepting...</>
                ) : (
                  'Accept invitation'
                )}
              </Button>
            </div>
          )}

          {user && !emailMatches && (
            <div className="space-y-3">
              <Alert variant="destructive">
                <AlertTitle>Wrong account</AlertTitle>
                <AlertDescription>
                  This invitation was sent to <strong>{inviteeEmail}</strong> but you're signed in as{' '}
                  <strong>{user.email}</strong>. Sign out and sign in with the invited email.
                </AlertDescription>
              </Alert>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={async () => { await supabase.auth.signOut(); }}>
                  Sign out
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => navigate('/')}>
                  Go home
                </Button>
              </div>
            </div>
          )}

          {!user && (
            <div className="space-y-5">
              <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
                <div>
                  <div className="font-semibold text-sm mb-3">Create your account & accept</div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Email</Label>
                  <Input value={inviteeEmail} readOnly disabled />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Full name</Label>
                  <Input defaultValue={inviteeName} {...signupForm.register('fullName')} placeholder="Your name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Password</Label>
                  <div className="relative">
                    <Input type={showPw ? 'text' : 'password'} {...signupForm.register('password')} />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowPw((v) => !v)}
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {signupForm.formState.errors.password && (
                    <p className="text-xs text-destructive mt-1">
                      {signupForm.formState.errors.password.message}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Confirm password</Label>
                  <div className="relative">
                    <Input type={showConfirmPw ? 'text' : 'password'} {...signupForm.register('confirmPassword')} />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowConfirmPw((v) => !v)}
                    >
                      {showConfirmPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {signupForm.formState.errors.confirmPassword && (
                    <p className="text-xs text-destructive mt-1">
                      {signupForm.formState.errors.confirmPassword.message}
                    </p>
                  )}
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="agree"
                    className="mt-0.5 relative z-10"
                    checked={signupForm.watch('agree') === true}
                    onCheckedChange={(c) =>
                      signupForm.setValue('agree', c === true ? true : (undefined as any), { shouldValidate: true })
                    }
                  />
                  <Label
                    htmlFor="agree"
                    className="text-xs leading-snug cursor-pointer select-none"
                  >
                    I agree to the RLevents terms of service
                  </Label>
                </div>
                {signupForm.formState.errors.agree && (
                  <p className="text-xs text-destructive">{signupForm.formState.errors.agree.message}</p>
                )}
                {signupError && (
                  <Alert variant={emailExists ? 'default' : 'destructive'}>
                    <AlertDescription>
                      {signupError}
                      {emailExists && (
                        <span className="mt-1 block text-xs font-medium">
                          ↓ Use the “Sign in” button below.
                        </span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                  disabled={signupForm.formState.isSubmitting || signupForm.watch('agree') !== true}
                >
                  {signupForm.formState.isSubmitting ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating...</>
                  ) : (
                    'Create account & accept'
                  )}
                </Button>
              </form>

              <div className="relative">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">
                  or
                </span>
              </div>

              <div
                className={
                  'text-center space-y-2 rounded-lg transition-all ' +
                  (emailExists
                    ? 'ring-2 ring-secondary ring-offset-2 ring-offset-card bg-secondary/5 p-4 animate-in fade-in'
                    : 'p-0')
                }
              >
                <p className="text-sm text-muted-foreground">Already have an account?</p>
                <Button
                  variant={emailExists ? 'default' : 'outline'}
                  className={
                    'w-full ' +
                    (emailExists
                      ? 'bg-secondary hover:bg-secondary/90 text-secondary-foreground'
                      : '')
                  }
                  onClick={() =>
                    navigate(
                      `/login?redirect=/invites/admin/${token}&email=${encodeURIComponent(inviteeEmail)}`
                    )
                  }
                >
                  Sign in
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Wrapper>
  );
}
